import { useCallback } from 'react';
import { fetchSignedUrlByType } from '../../utils/fetchSignedUrlByFolderV2';

/**
 * Hook that provides utility functions for voice actor management
 * @returns {Object} Object containing utility functions
 */
export function useVoiceActorUtilities() {
  /**
   * Extracts actor, portfolio, character, variation UUIDs and filename from an S3 URL
   * @param {string} url - The S3 URL to parse
   * @param {string} userId - The user ID to include in the result
   * @returns {Object} Object containing all extracted parameters
   */
  const extractParamsFromS3Url = useCallback((url, userId) => {
    // Split the URL into segments
    const segments = url.split('/');

    // Extract the filename (last segment)
    const fileName = segments.pop();

    // Find indices of key markers in the URL
    const userIndex = segments.findIndex(segment => segment === 'aai-users');
    const actorIndex = segments.findIndex(segment => segment === 'aai-actors');
    const portfolioIndex = segments.findIndex(segment => segment === 'act-portfolios');
    const characterIndex = segments.findIndex(segment => segment === 'act-characters');
    const variationIndex = segments.findIndex(segment => segment === 'act-variations');

    // Extract UUIDs (they follow each marker)
    const extractedUserId = userIndex >= 0 && userIndex + 1 < segments.length ? segments[userIndex + 1] : null;
    const actorUuid = actorIndex >= 0 && actorIndex + 1 < segments.length ? segments[actorIndex + 1] : null;
    const portfolioUuid = portfolioIndex >= 0 && portfolioIndex + 1 < segments.length ? segments[portfolioIndex + 1] : null;
    const characterUuid = characterIndex >= 0 && characterIndex + 1 < segments.length ? segments[characterIndex + 1] : null;
    const variationUuid = variationIndex >= 0 && variationIndex + 1 < segments.length ? segments[variationIndex + 1] : null;

    // Construct and return the result object
    return {
      requestType: 'VOICE_FILE',
      userId: userId || extractedUserId, // Use provided userId or extract from URL
      actorUuid,
      portfolioUuid,
      characterUuid,
      variationUuid,
      fileName
    };
  }, []);

  /**
   * Gets a signed URL for a voice actor audio file
   * @param {string} s3Url - The S3 URL to get a signed URL for
   * @param {string} userId - The user ID
   * @returns {Promise<string>} A promise that resolves to the signed URL
   */
  const getSignedUrl = useCallback(async (s3Url, userId) => {
    if (!s3Url) {
      console.log('No s3Url provided, returning empty string');
      return '';
    }

    try {
      console.log('Getting signed URL for:', s3Url);
      const params = extractParamsFromS3Url(s3Url, userId);
      console.log('Extracted params:', params);

      const url = await fetchSignedUrlByType(params);
      console.log('Received signed URL:', url);

      return url || '';
    } catch (err) {
      console.error('Error getting signed URL:', err);
      return '';
    }
  }, [extractParamsFromS3Url]);

  return {
    extractParamsFromS3Url,
    getSignedUrl
  };
}
